import {escapeRegExp, Relationship} from "@packages/commons";
import Tokenizr, {type Token as BaseToken} from "tokenizr";
import type CompilerScope from "./CompilerScope";
import { CURRENCY_SYMBOLS, CURRENCY_SYMBOLS_REGEX } from "./DeclaredValue";

export interface Token extends Omit<BaseToken, "tag" | "isA"> {
  type:
    | "date"
    | "time"
    | "ws"
    | "start_arg"
    | "end_arg"
    | "separator"
    | "relationship"
    | "word"
    | "number"
    | "quoted_phrase"
    | "start_comment"
    | "end_comment"
    | "boolean"
    | "parsed"
    | "uncertain"
    | "attribute";
  pairId?: number | null;
}

export interface TokenizerOptions {
  scope: CompilerScope;
  relationships: Relationship[];
}

export default class Tokenizer {
  private readonly lexer = new Tokenizr();

  constructor(options: TokenizerOptions) {
    this.lexer.rule(/["“”]([^"“”]*([\\.][^"“”]*)*)["“”]/i, (ctx, match) => {
      const text = match[1];
      const trimmedText = text.trim();
      const number = Number(trimmedText);
      if (trimmedText.length === 0 || Number.isNaN(number)) {
        ctx.accept("quoted_phrase", text);
      } else {
        // keep the text as written so it can be used as-is when compared with a text attribute
        ctx.accept("number", {value: number, quoted: true, text});
      }
    });
    // biome-ignore lint/suspicious/noControlCharactersInRegex: we want to match control characters
    this.lexer.rule(/\x00+/, (ctx, match) => {
      ctx.accept("parsed");
    });
    this.lexer.rule(/\d{4}(\/|-)(0[1-9]|1[120])(\/|-)(0[1-9]|[12][0-9]|3[01])/i, (ctx, match) => {
      ctx.accept("date", match[0]);
    });
    /*this.lexer.rule(/(?:[01]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?/i, (ctx, match) => {
      ctx.accept("time", match[0]);
    });*/
    this.lexer.rule(
      new RegExp(`[-${CURRENCY_SYMBOLS.join("")}]?\\d{1,}(?:\\,?\\d{3})*(?:\\.\\d+)?%?`),
      (ctx, match) => {
        let negative = false;
        let text = match[0];
        if (text.startsWith("-")) {
          text = text.substring(1);
          negative = true;
        }
        ctx.accept("number", {
          value: Number.parseFloat(text.replace(CURRENCY_SYMBOLS_REGEX, "").replace(/,/g, "")) * (negative ? -1 : 1),
          quoted: false,
        });
      },
    );
    // number without leading 0 (eg .01)
    this.lexer.rule(/-?[$£€]?(\.\d*)/i, (ctx, match) => {
      const negative = match[0].startsWith("-");
      ctx.accept("number", {
        value: Number.parseFloat(match[1]) * (negative ? -1 : 1),
        quoted: false,
      });
    });
    this.lexer.rule(/false|true/i, (ctx, match) => {
      ctx.accept("boolean", match[0]?.toLowerCase() === "true");
    });
    this.lexer.rule(/uncertain/i, (ctx, match) => {
      ctx.accept("uncertain", null);
    });
    this.lexer.rule(/\s/i, (ctx, match) => {
      ctx.accept("ws");
    });
    this.lexer.rule(/,/i, (ctx, match) => {
      ctx.accept("separator");
    });
    this.lexer.rule(/\(/i, (ctx, match) => {
      ctx.accept("start_arg");
    });
    this.lexer.rule(/\)/i, (ctx, match) => {
      ctx.accept("end_arg");
    });
    this.lexer.rule(/\[/i, (ctx, match) => {
      ctx.accept("start_comment");
    });
    this.lexer.rule(/]/i, (ctx, match) => {
      ctx.accept("end_comment");
    });

    for (const relationship of options.relationships) {
      if (relationship.text) {
        this.lexer.rule(new RegExp(escapeRegExp(relationship.text), "i"), (ctx, match) => {
          ctx.accept("relationship", {id: relationship.id});
        });
      }
      /*if (relationship.textSingular) {
        this.lexer.rule(new RegExp(escapeRegExp(relationship.textSingular), "i"), (ctx, match) => {
          ctx.accept("relationship", relationship.id);
        });
      }*/
      if (relationship.reverseText) {
        this.lexer.rule(new RegExp(escapeRegExp(relationship.reverseText), "i"), (ctx, match) => {
          ctx.accept("relationship", {id: relationship.id, reverse: true});
        });
      }
      /*const entityRegex = createEntityRegex(relationship);
      if (entityRegex) {
        this.lexer.rule(entityRegex, (ctx, match) => {
          ctx.accept("entity_identifier", relationship.name);
        });
      }*/
    }

    const attributes = options.scope.getAllCapturedAttributes();
    attributes.sort((a, b) => (a.description && b.description ? b.description.length - a.description.length : 0));
    for (const attribute of attributes) {
      if (!attribute.description) continue;
      this.lexer.rule(new RegExp(escapeRegExp(attribute.description), "i"), (ctx, match) => {
        ctx.accept("attribute", attribute.id);
      });
    }

    this.lexer.rule(/[a-zA-Z_][a-zA-Z0-9_-]*/, (ctx, match) => {
      ctx.accept("word");
    });
    this.lexer.rule(/./i, (ctx, match) => {
      ctx.accept("char");
    });
  }

  tokenize(input: string, tokenOffset = 0): Token[] {
    if (!input) return [];
    const result = this.lexer
      .input(input)
      .tokens()
      .map((token) => {
        token.column += tokenOffset;
        token.pos += tokenOffset;
        return token;
      }) as Token[];
    //console.log(result);
    return result;
  }
}

export class TokenQueue {
  index = 0;
  private length;
  readonly tokens: Token[];

  constructor(tokens: Token[]) {
    this.tokens = tokens.filter((token) => (token as any).type !== "EOF");
    this.length = this.tokens.length;
  }

  peek(): Token | undefined {
    return this.tokens[this.index];
  }

  pop(): Token | undefined {
    return this.tokens[this.index++];
  }

  hasNext(): boolean {
    return this.index < this.length;
  }
}
